import React from 'react';

import { type HexAlpha, type UUID, isDefined, isEmpty, silenceError } from '@nfctron/nexus-core';
import { type ExtendedComponent, cn, useContextRequired, useFormat } from '@nfctron/nexus-react';

import { ActionIcon } from '@mantine/core';
import { openConfirmModal } from '@mantine/modals';
import { TrashIcon } from 'lucide-react';

import { getEventTypeSensitiveComponentDisplayName } from '@root/pages/event/[organizer]/[event]/_resources/helpers/common';

import { NumberInput } from '@root/features/form/components/NumberInput/NumberInput';
import { TextInput, getTextFieldErrorComponent } from '@root/features/form/components/TextInput/TextInput';

import { ChipControl } from '@root/lib/components/ChipControl/ChipControl';
import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { useTranslate } from '@root/lib/hooks/useTranslation';
import { useTracking } from '@root/lib/modules/tracking/TrackingProvider';

import { useCartStore, useCartedTicketForm } from '../../stores/cartStore';
import { EventCartPageContext } from '../CartContext';

import { TicketCardDivider } from './TicketCardDivider';

/**
 * Event/Cart/TicketCardConfig component
 * @constructor
 */
export const TicketCardConfig: React.FunctionComponent<TicketCardConfigProps> = ({
	readOnly = false,
	cartTicketId,
	dividerBgColor = '#FFF',
	className,
	...props
}) => {
	const format = useFormat();
	const { event } = useContextRequired(EventCartPageContext);
	const { t } = useTranslate(APP_I18N_NAMESPACE.MAIN, TicketCardConfig.displayName);
	const { trackingModule } = useTracking();
	const { t: typeSensitiveT } = useTranslate(
		APP_I18N_NAMESPACE.MAIN,
		getEventTypeSensitiveComponentDisplayName(TicketCardConfig.displayName, event.eventType.eventTypeId),
	);
	const removeCartedTickets = useCartStore((s) => s.removeCartedTickets);
	const cartedTicketForm = useCartedTicketForm(cartTicketId);

	/** user input section validity state */
	const isUserInputSection = React.useMemo(() => {
		const isHolderNameValid = cartedTicketForm.isFieldValid('holderName');
		const isHolderNameTouched = cartedTicketForm.isFieldTouched('holderName');
		const isUserDataValid = cartedTicketForm.ticketType?.userData?.fields?.every((field) =>
			cartedTicketForm.isFieldValid(`userData.${field.fieldId}`),
		);
		const isUserDataTouched = cartedTicketForm.isFieldTouched('userData');
		return {
			valid: isHolderNameValid && isUserDataValid,
			touched: isHolderNameTouched || isUserDataTouched,
		};
	}, [cartedTicketForm]);

	const seat = useCartStore((s) => s.getSeatFromCartedTicket({ cartTicketId }));
	const crowd = useCartStore((s) => s.getCrowdFromCartedTicket({ cartTicketId }));
	const getSectorById = useCartStore((s) => s.getSectorById);
	const sector = React.useMemo(() => {
		return silenceError(() => getSectorById({ sectorId: isDefined(crowd) ? crowd.sectorId : isDefined(seat) ? seat.sectorId : '' }), null);
	}, [getSectorById, crowd, seat]);

	const seatingControlNode = React.useMemo(() => {
		/** no seating control if ticket type is not reservable */
		if (!cartedTicketForm.ticketType.isReservable) return null;

		/** get any reservation error message */
		const errorMessage =
			cartedTicketForm.getFieldError('reservation') ||
			cartedTicketForm.getFieldError('reservation.seat') ||
			cartedTicketForm.getFieldError('reservation.seat.seatId') ||
			cartedTicketForm.getFieldError('reservation.seat.reservationSeatId') ||
			cartedTicketForm.getFieldError('reservation.crowd') ||
			cartedTicketForm.getFieldError('reservation.crowd.crowdId') ||
			cartedTicketForm.getFieldError('reservation.crowd.reservationCrowdId');

		return (
			<div className="flex flex-col gap-4">
				<div>
					<div className="grid grid-cols-3 gap-3">
						{/* sector */}
						<div>
							<span className="block text-base text-zinc-500">{t('seating.sector.label')}</span>
							<span className="text-lucky-point-500 block text-xl md:text-2xl">{isDefined(sector) ? sector.label : '--'}</span>
						</div>
						{/* crowd */}
						{isDefined(crowd) ? (
							<div className="col-span-2">
								<span className="block text-base text-zinc-500">{t('seating.crowd.label')}</span>
								<span className="text-lucky-point-500 block text-xl md:text-2xl">{crowd.name}</span>
							</div>
						) : (
							<React.Fragment>
								{/* row */}
								<div>
									<span className="block text-base text-zinc-500">{t('seating.seatRow.label')}</span>
									<span className="text-lucky-point-500 block text-xl md:text-2xl">{isDefined(seat) ? seat.seatRow : '--'}</span>
								</div>
								{/* seat */}
								<div>
									<span className="block text-base text-zinc-500">{t('seating.seatPlace.label')}</span>
									<span className="text-lucky-point-500 block text-xl">{isDefined(seat) ? seat.label : '--'}</span>
								</div>
							</React.Fragment>
						)}
					</div>

					{/* error message */}
					{!isEmpty(errorMessage) && <span className="text-xs text-red-500">{getTextFieldErrorComponent(errorMessage)}</span>}
				</div>
			</div>
		);
	}, [cartedTicketForm, crowd, seat, sector, t]);

	return (
		<div className={cn('rounded-xl border border-gray-200 bg-gray-50! pb-2', className)} {...props}>
			{/* ticket header */}
			<header className="flex items-start justify-between gap-3 p-4 pb-2">
				<div>
					{/* ticket type name */}
					<span className="text-lucky-point-500 text-xl font-semibold xl:text-2xl">{cartedTicketForm.ticketType.namePub}</span>
					{/* ticket price */}
					<div>
						<span className="font-medium text-zinc-700">
							{format.price(cartedTicketForm.ticketType.price, event.currency.baseCurrency.currencyId)}
						</span>
						{/* original price */}
						{isDefined(cartedTicketForm.ticketType.extraData.originalPrice) &&
							cartedTicketForm.ticketType.extraData.originalPrice > cartedTicketForm.ticketType.price && (
								<span className="ml-1 text-sm text-zinc-500 line-through">
									{format.price(cartedTicketForm.ticketType.extraData.originalPrice, event.currency.baseCurrency.currencyId)}
								</span>
							)}
					</div>
				</div>

				{/* delete icon button */}
				{cartedTicketForm.ticketType.extraData.addToCartQty === 1 && cartedTicketForm.ticketType.__isListable && (
					<ActionIcon
						variant="transparent"
						radius="xl"
						onClick={() => {
							openConfirmModal({
								title: typeSensitiveT('removeConfirmation.title'),
								centered: true,
								children: <span className="text-base">{typeSensitiveT('removeConfirmation.text')}</span>,
								labels: {
									confirm: t('removeConfirmation.confirmButton.text'),
									cancel: t('removeConfirmation.cancelButton.text'),
								},
								confirmProps: { color: 'pink' },
								cancelProps: { color: 'zinc', variant: 'light' },
								onConfirm: async () => {
									/** remove ticket from cart */
									removeCartedTickets({
										cartTicketIds: [cartedTicketForm.values.cartTicketId],
										trackingModule,
									});
								},
							});
						}}
						color="red"
					>
						<TrashIcon size={19} />
					</ActionIcon>
				)}
			</header>

			{/* divider */}
			<TicketCardDivider bgColor={dividerBgColor} />

			{/* user input section */}
			<section
				className={cn([
					'flex grow flex-col gap-4 px-4 py-2',
					'border-l-2 border-gray-300',
					isUserInputSection.touched && [!isUserInputSection.valid && 'border-red-500!', isUserInputSection.valid && 'border-green-500!'],
				])}
			>
				{/* holderName input */}
				<TextInput
					error={cartedTicketForm.getFieldError('holderName')}
					value={cartedTicketForm.getFieldValue('holderName')}
					onChange={(e) => cartedTicketForm.setFieldValue('holderName', e.target.value)}
					onBlur={() => cartedTicketForm.setFieldTouched('holderName')}
					withAsterisk={cartedTicketForm.ticketType.isHolderRequired}
					/** holderName is required */
					{...(cartedTicketForm.ticketType.isHolderRequired && {
						disabled: cartedTicketForm.ticketType.__isImported,
						label: typeSensitiveT('holderRequired.label'),
						placeholder: typeSensitiveT('holderRequired.placeholder'),
					})}
					/** holderName is optional */
					{...(!cartedTicketForm.ticketType.isHolderRequired && {
						disabled: cartedTicketForm.ticketType.__isImported,
						label: typeSensitiveT('holderOptional.label'),
						placeholder: typeSensitiveT('holderOptional.placeholder'),
						description: typeSensitiveT('holderOptional.description'),
					})}
					/** readOnly */
					{...(readOnly && {
						readOnly: true,
						disabled: true,
					})}
				/>

				{/* extra userData fields */}
				{isDefined(cartedTicketForm.ticketType.userData) && cartedTicketForm.ticketType.userData?.fields.length > 0 && (
					<React.Fragment>
						{cartedTicketForm.ticketType.userData.fields.map((field) => {
							/** common field params */
							const params = {
								key: field.fieldId,
								label: field.label,
								description: field.description,
								placeholder: field.placeholder ?? t('userData.placeholder'),
								readOnly: readOnly ?? field.readOnly,
								disabled: field.disabled,
								value: cartedTicketForm.getFieldValue(`userData.${field.fieldId}`),
								error: cartedTicketForm.getFieldError(`userData.${field.fieldId}`),
								onBlur: () => cartedTicketForm.setFieldTouched(`userData.${field.fieldId}`),
							} as const;

							switch (field.type) {
								/** text field */
								case 'text': {
									return (
										<TextInput
											{...params}
											key={params.key}
											onChange={(e) => cartedTicketForm.setFieldValue(`userData.${field.fieldId}`, e.target.value)}
										/>
									);
								}
								/** number field */
								case 'number': {
									return (
										<NumberInput
											{...params}
											key={params.key}
											onChange={(value) => cartedTicketForm.setFieldValue(`userData.${field.fieldId}`, value)}
										/>
									);
								}
								default: {
									return null;
								}
							}
						})}
					</React.Fragment>
				)}
			</section>

			{/* seats section */}
			{isDefined(seatingControlNode) && (
				<section
					className={cn([
						'flex grow flex-col gap-4 px-4 py-2',
						'border-l-2 border-gray-300',
						cartedTicketForm.isFieldTouched('reservation') && [
							cartedTicketForm.isFieldValid('reservation') && 'border-green-500!',
							!cartedTicketForm.isFieldValid('reservation') && 'border-red-500!',
						],
					])}
				>
					{seatingControlNode}
				</section>
			)}

			{/* chips section */}
			{cartedTicketForm.values.chips.length > 0 && cartedTicketForm.ticketType.creditLimit > cartedTicketForm.ticketType.creditMinimum && (
				<React.Fragment>
					{/* divider */}
					<TicketCardDivider bgColor={dividerBgColor} />

					{/* top-uppable chip list */}
					<section
						className={cn([
							'flex grow flex-col gap-4 px-4 py-2',
							'border-l-2 border-gray-300',
							cartedTicketForm.isFieldTouched('chips') && [
								cartedTicketForm.isFieldValid('chips') && 'border-green-500!',
								!cartedTicketForm.isFieldValid('chips') && 'border-red-500!',
							],
						])}
					>
						{cartedTicketForm.values.chips.map((chip, chipIndex) => (
							<ChipControl
								readOnly={readOnly}
								key={chip.cartChipId}
								minCharge={cartedTicketForm.ticketType.creditMinimum}
								maxCharge={cartedTicketForm.ticketType.creditLimit}
								/** allow zeroCharge only for normal tickets, inserted (for top-up) cannot be charged for zero amount */
								allowZeroCharge={!cartedTicketForm.ticketType.__isImported}
								currency={event.currency}
								value={chip.amount}
								isTouched={cartedTicketForm.isFieldTouched(`chips.${chipIndex}.amount`)}
								isValid={cartedTicketForm.isFieldValid(`chips.${chipIndex}.amount`)}
								error={cartedTicketForm.getFieldError(`chips.${chipIndex}.amount`)}
								onChange={(value) => cartedTicketForm.setFieldValue(`chips.${chipIndex}.amount`, value)}
								onBlur={() => cartedTicketForm.setFieldTouched(`chips.${chipIndex}.amount`)}
							/>
						))}
					</section>
				</React.Fragment>
			)}
		</div>
	);
};

/**
 * Event/Cart/TicketCardConfig component props type
 * @export
 */
export type TicketCardConfigProps = ExtendedComponent<{ readOnly?: boolean; cartTicketId: UUID; dividerBgColor?: HexAlpha }>;
TicketCardConfig.displayName = 'Event/Cart/TicketCardConfig';
